import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsLoggerPlugin } from '@ngxs/logger-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore, Store } from '@ngxs/store';
import { AuthState } from '@taxora-business-auth/data-access/state/auth-state';
import { AppState } from '@taxora-business-core/data-access/state/app.state';
import { InvitationsState } from '@taxora-business-invitations/data-access/state/invitations.state';
import { OrganizationState } from '@taxora-business-organization/data-access/state/organization.state';
import { ProvidersState } from '@taxora-business-providers/data-access/state/providers.state';
import { BusinessDocumentsState } from '@taxora-business-documents/data-access/state/documents.state';
import { environment } from '@taxora-business/environments/environment';
import { provideApiConfiguration } from '@taxora-api';
import {
  ACCESS_TOKEN,
  httpErrorInterceptor,
  loginInterceptor,
} from '@taxora-shared-core/data-access';
import { MultiTranslateLoader } from '@taxora-shared-core/i18n';
import { Language, TAXORA_PRESET } from '@taxora-shared-core/util';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';

function translateLoaderFactory(http: HttpClient): TranslateLoader {
  return new MultiTranslateLoader(http, [
    { projectName: 'taxora-shared', moduleName: 'forms' },
    { projectName: 'taxora-shared', moduleName: 'common' },
    { projectName: 'taxora-business', moduleName: 'auth' },
    { projectName: 'taxora-business', moduleName: 'core' },
    { projectName: 'taxora-business', moduleName: 'invitations' },
    { projectName: 'taxora-business', moduleName: 'organization' },
    { projectName: 'taxora-business', moduleName: 'providers' },
    { projectName: 'taxora-business', moduleName: 'documents' },
  ]);
}

function initTranslations(translate: TranslateService) {
  translate.setFallbackLang(Language.Ro);
  return firstValueFrom(translate.use(Language.Ro));
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ACCESS_TOKEN,
      useFactory: () => () => inject(Store).selectSnapshot(AppState.accessToken),
    },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([loginInterceptor, httpErrorInterceptor])),
    provideApiConfiguration(environment.apiUrl),
    providePrimeNG({
      theme: {
        preset: TAXORA_PRESET,
      },
    }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: translateLoaderFactory,
        deps: [HttpClient],
      },
      fallbackLang: Language.Ro,
    }),
    provideStore(
      [
        AppState,
        AuthState,
        InvitationsState,
        OrganizationState,
        ProvidersState,
        BusinessDocumentsState,
      ],
      withNgxsLoggerPlugin(),
      withNgxsReduxDevtoolsPlugin(),
      withNgxsStoragePlugin({
        keys: ['app.accessToken', 'app.user', 'app.currentOrganizationId'],
      }),
    ),
    MessageService,
    provideAppInitializer(() => initTranslations(inject(TranslateService))),
  ],
};
